export const SENSITIVE_API_LIST = [
  // 收货地址（TDC storefront）。统一走 /p/store/address/*，与区域/商品等 C 端入口一致。
  {
    method: 'POST',
    pathPattern: /^(?:\/api)?\/p\/store\/address\/addAddr$/,
    requestSensitive: true,
    responseSensitive: true,
  },
  {
    method: 'PUT',
    pathPattern: /^(?:\/api)?\/p\/store\/address\/updateAddr$/,
    requestSensitive: true,
    responseSensitive: true,
  },
  // 钱包仍在 mall4j（TDC 未迁该域）；待钱包域迁入 TDC 后随之切换公钥与端点。
  { method: 'GET', path: '/p/wallet/info', requestSensitive: false, responseSensitive: true },
  {
    method: 'GET',
    pathPattern: /^(?:\/api)?\/p\/store\/address\/list$/,
    requestSensitive: false,
    responseSensitive: true,
  },
  {
    method: 'GET',
    pathPattern: /^(?:\/api)?\/p\/store\/address\/addrInfo\/[^/]+$/,
    requestSensitive: false,
    responseSensitive: true,
  },
  {
    method: 'POST',
    pathPattern: /^(?:\/api)?\/store\/ocr\/recognize$/,
    requestSensitive: true,
    responseSensitive: true,
  },
  {
    method: 'GET',
    pathPattern: /^(?:\/api)?\/store\/channel-certification\/latest$/,
    requestSensitive: false,
    responseSensitive: true,
  },
  {
    method: 'GET',
    pathPattern: /^(?:\/api)?\/store\/channel-certification\/engineering\/draft$/,
    requestSensitive: false,
    responseSensitive: true,
  },
  {
    method: 'POST',
    pathPattern:
      /^(?:\/api)?\/store\/channel-certification\/engineering\/(?:check-existing|draft|send-code|submit)$/,
    requestSensitive: true,
    responseSensitive: true,
  },
]

const ALGORITHM = 'RSA_OAEP_AES_GCM'
const PUBLIC_KEY_PATH = '/crypto/publicKey'
const PUBLIC_KEY_CACHE_TTL = 10 * 60 * 1000

const RESPONSE_CONTEXT_HEADER_ALG = 'X-Sensitive-Alg'
const RESPONSE_CONTEXT_HEADER_KEY_VERSION = 'X-Sensitive-Key-Version'
const RESPONSE_CONTEXT_HEADER_ENCRYPTED_KEY = 'X-Sensitive-Encrypted-Key'
const RESPONSE_CONTEXT_HEADER_TIMESTAMP = 'X-Sensitive-Timestamp'
const RESPONSE_CONTEXT_HEADER_NONCE = 'X-Sensitive-Nonce'

const publicKeyCache = new Map()
const textEncoder = new TextEncoder()
const textDecoder = new TextDecoder()

/**
 * @description 匹配敏感接口定义
 * @param {string} url - 请求地址
 * @param {string} [method='GET'] - 请求方法
 * @returns {Object|null}
 */
export function matchSensitiveApi(url, method = 'GET') {
  const normalizedMethod = String(method || 'GET').toUpperCase()
  const normalizedPath = normalizePath(url)

  return (
    SENSITIVE_API_LIST.find((item) => {
      if (String(item.method || 'GET').toUpperCase() !== normalizedMethod) {
        return false
      }
      if (item.path) {
        return item.path === normalizedPath
      }
      if (item.pathPattern instanceof RegExp) {
        return item.pathPattern.test(normalizedPath)
      }
      return false
    }) || null
  )
}

/**
 * @description 判断载荷是否为敏感响应信封
 * @param {unknown} payload - 响应数据
 * @returns {boolean}
 */
export function isSensitiveEnvelope(payload) {
  return !!(
    payload &&
    typeof payload === 'object' &&
    payload.alg === ALGORITHM &&
    payload.keyVersion &&
    payload.iv &&
    payload.ciphertext &&
    payload.tag
  )
}

/**
 * @description 为敏感接口准备请求体与响应协商上下文
 * @param {Object} options - 请求准备参数
 * @param {string} options.url - 请求地址
 * @param {string} [options.method='GET'] - 请求方法
 * @param {unknown} options.data - 原始请求体
 * @param {string} [options.apiBase=''] - API baseURL
 * @param {Object} [options.headers={}] - 原始请求头
 * @returns {Promise<{data: unknown, context: Object|null, sensitiveApi: Object|null, headers: Record<string,string>}>}
 */
export async function prepareSensitiveRequest({
  url,
  method = 'GET',
  data,
  apiBase = '',
  headers = {},
}) {
  const sensitiveApi = matchSensitiveApi(url, method)
  if (!sensitiveApi || (!sensitiveApi.requestSensitive && !sensitiveApi.responseSensitive)) {
    return {
      data,
      context: null,
      sensitiveApi,
      headers: {},
    }
  }

  const publicKeyInfo = await getPublicKey(apiBase, headers)
  const context = await createContext(publicKeyInfo)

  return {
    data: sensitiveApi.requestSensitive
      ? await encryptPayload(data ?? {}, publicKeyInfo, context)
      : data,
    context: sensitiveApi.responseSensitive ? context : null,
    sensitiveApi,
    headers:
      sensitiveApi.responseSensitive && !sensitiveApi.requestSensitive
        ? buildResponseContextHeaders(publicKeyInfo, context)
        : {},
  }
}

/**
 * @description 解密敏感响应
 * @param {Object} payload - 响应信封
 * @param {Object|null} context - 响应解密上下文
 * @returns {Promise<unknown>}
 */
export async function decryptSensitiveResponse(payload, context) {
  if (!isSensitiveEnvelope(payload)) {
    return payload
  }
  if (!context) {
    throw new Error('敏感响应缺少解密上下文')
  }

  const plainText = await decryptPayload(payload, context)
  try {
    return JSON.parse(plainText)
  } catch (error) {
    return plainText
  }
}

/**
 * @description 清理公钥缓存
 * @param {string} [apiBase=''] - API baseURL
 */
export function clearSensitivePublicKeyCache(apiBase = '') {
  publicKeyCache.delete(normalizeApiBase(apiBase))
}

async function getPublicKey(apiBase = '', headers = {}) {
  const cacheKey = normalizeApiBase(apiBase)
  const now = Date.now()
  const cached = publicKeyCache.get(cacheKey)

  if (cached?.data && cached.expiresAt > now) {
    return cached.data
  }
  if (cached?.pending) {
    return cached.pending
  }

  const pending = fetch(joinUrl(cacheKey, PUBLIC_KEY_PATH), {
    method: 'GET',
    headers: buildPublicKeyHeaders(headers),
    credentials: 'same-origin',
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`获取公钥失败: ${response.status}`)
      }
      const payload = await response.json()
      const data = normalizePublicKeyPayload(payload)
      publicKeyCache.set(cacheKey, {
        data,
        expiresAt: Date.now() + PUBLIC_KEY_CACHE_TTL,
      })
      return data
    })
    .catch((error) => {
      publicKeyCache.delete(cacheKey)
      throw error
    })

  publicKeyCache.set(cacheKey, { pending })
  return pending
}

function normalizePublicKeyPayload(payload) {
  if (payload?.code && String(payload.code) !== '00000') {
    throw new Error(payload.msg || payload.message || '获取公钥失败')
  }

  const data = payload?.data || payload
  if (!data?.publicKey || !data?.keyVersion) {
    throw new Error('公钥响应缺少必要字段')
  }

  return {
    alg: data.alg || ALGORITHM,
    keyVersion: data.keyVersion,
    publicKey: data.publicKey,
  }
}

function buildPublicKeyHeaders(headers) {
  const normalizedHeaders = normalizeHeaders(headers)
  const result = {
    Locale: normalizedHeaders.Locale || normalizedHeaders.locale || 'zh_CN',
  }
  if (normalizedHeaders.registerSource) {
    result.registerSource = normalizedHeaders.registerSource
  }
  return result
}

function normalizeHeaders(headers = {}) {
  if (!headers) {
    return {}
  }
  if (typeof headers.toJSON === 'function') {
    return headers.toJSON()
  }
  return Object.keys(headers).reduce((result, key) => {
    result[key] = headers[key]
    return result
  }, {})
}

function normalizePath(url = '') {
  const rawValue = String(url || '').trim()
  if (!rawValue) {
    return ''
  }

  try {
    if (rawValue.startsWith('http://') || rawValue.startsWith('https://')) {
      return new URL(rawValue).pathname
    }
  } catch (error) {
    // ignore and fallback to string parsing
  }

  const queryIndex = rawValue.indexOf('?')
  const hashIndex = rawValue.indexOf('#')
  const endIndex =
    queryIndex === -1 ? hashIndex : hashIndex === -1 ? queryIndex : Math.min(queryIndex, hashIndex)
  return endIndex === -1 ? rawValue : rawValue.slice(0, endIndex)
}

function normalizeApiBase(apiBase = '') {
  return String(apiBase || '')
    .trim()
    .replace(/\/+$/, '')
}

function joinUrl(base, path) {
  if (!base) {
    return path
  }
  return `${base}${path.startsWith('/') ? path : `/${path}`}`
}

function buildResponseContextHeaders(publicKeyInfo, context) {
  return {
    [RESPONSE_CONTEXT_HEADER_ALG]: publicKeyInfo.alg || ALGORITHM,
    [RESPONSE_CONTEXT_HEADER_KEY_VERSION]: context.keyVersion,
    [RESPONSE_CONTEXT_HEADER_ENCRYPTED_KEY]: context.encryptedKey,
    [RESPONSE_CONTEXT_HEADER_TIMESTAMP]: String(Date.now()),
    [RESPONSE_CONTEXT_HEADER_NONCE]: randomNonce(),
  }
}

function ensureSubtle() {
  const subtle = globalThis?.crypto?.subtle
  if (!subtle) {
    throw new Error('当前环境不支持 Web Crypto API')
  }
  return subtle
}

function randomBytes(size) {
  const bytes = new Uint8Array(size)
  if (!globalThis?.crypto?.getRandomValues) {
    throw new Error('当前环境不支持安全随机数生成')
  }
  globalThis.crypto.getRandomValues(bytes)
  return bytes
}

function stringToUint8Array(value) {
  return textEncoder.encode(value)
}

function arrayBufferToString(buffer) {
  return textDecoder.decode(buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer))
}

function toBase64(input) {
  const bytes =
    input instanceof Uint8Array
      ? input
      : input instanceof ArrayBuffer
        ? new Uint8Array(input)
        : new Uint8Array(input.buffer, input.byteOffset, input.byteLength)

  let binary = ''
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte)
  })
  return btoa(binary)
}

function fromBase64(base64) {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

function toBase64Url(input) {
  return toBase64(input).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

function randomNonce() {
  return toBase64Url(randomBytes(16))
}

function stripPem(pem) {
  return String(pem || '')
    .replace(/-----BEGIN [^-]+-----/g, '')
    .replace(/-----END [^-]+-----/g, '')
    .replace(/\s+/g, '')
}

async function importPublicKey(publicKeyPem) {
  return ensureSubtle().importKey(
    'spki',
    fromBase64(stripPem(publicKeyPem)),
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256',
    },
    false,
    ['encrypt']
  )
}

async function createContext(publicKeyInfo) {
  const aesKeyRaw = randomBytes(32)
  const publicKey = await importPublicKey(publicKeyInfo.publicKey)
  const encryptedKey = await ensureSubtle().encrypt({ name: 'RSA-OAEP' }, publicKey, aesKeyRaw)

  return {
    adapter: 'webcrypto',
    aesKeyRaw,
    encryptedKey: toBase64(encryptedKey),
    keyVersion: publicKeyInfo.keyVersion,
  }
}

async function encryptPayload(payload, publicKeyInfo, context) {
  const subtle = ensureSubtle()
  const iv = randomBytes(12)
  const aesKey = await subtle.importKey('raw', context.aesKeyRaw, { name: 'AES-GCM' }, false, [
    'encrypt',
  ])
  const encryptedBuffer = await subtle.encrypt(
    {
      name: 'AES-GCM',
      iv,
    },
    aesKey,
    stringToUint8Array(JSON.stringify(payload))
  )
  const encryptedBytes = new Uint8Array(encryptedBuffer)
  const tagLength = 16
  const ciphertext = encryptedBytes.slice(0, encryptedBytes.length - tagLength)
  const tag = encryptedBytes.slice(encryptedBytes.length - tagLength)

  return {
    alg: publicKeyInfo.alg || ALGORITHM,
    keyVersion: publicKeyInfo.keyVersion,
    encryptedKey: context.encryptedKey,
    iv: toBase64(iv),
    ciphertext: toBase64(ciphertext),
    tag: toBase64(tag),
    timestamp: Date.now(),
    nonce: randomNonce(),
  }
}

async function decryptPayload(payload, context) {
  const subtle = ensureSubtle()
  const aesKey = await subtle.importKey('raw', context.aesKeyRaw, { name: 'AES-GCM' }, false, [
    'decrypt',
  ])
  const ciphertext = fromBase64(payload.ciphertext)
  const tag = fromBase64(payload.tag)
  const encryptedBytes = new Uint8Array(ciphertext.length + tag.length)
  encryptedBytes.set(ciphertext, 0)
  encryptedBytes.set(tag, ciphertext.length)

  const plainBuffer = await subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: fromBase64(payload.iv),
    },
    aesKey,
    encryptedBytes
  )

  return arrayBufferToString(plainBuffer)
}
